import type {
  CreateWebsitePageRequest,
  UpdateWebsitePageRequest,
  WebsitePageDetail,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Admin Website Pages API. Mirrors the users.api.ts pattern (shared axios
 * client + react-query in the page). The backend returns the standard
 * `{ data, pagination }` list envelope and the WebsitePageDetail shape.
 */

export interface PageListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  pageType?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface PageListResponse {
  data: WebsitePageDetail[];
  pagination: { page: number; pageSize: number; totalItems: number };
}

export interface PageSection {
  id: string;
  sectionType: string;
  sortOrder: number;
  title: string | null;
  subtitle: string | null;
  isVisible: boolean;
  data: unknown;
}

export type PageDetailWithSections = WebsitePageDetail & { sections: PageSection[] };

/**
 * Default endpoint (Custom Pages). Tourism Boards and Landing Pages pass their
 * own `basePath` (e.g. `/admin/website/tourism-boards`) so the request hits the
 * type-scoped, separately-permissioned controller. Section editing is not
 * parameterised - it always runs through `/admin/website/sections`.
 */
export const PAGES_BASE = '/admin/website/pages';

export const listPages = async (
  query: PageListQuery,
  basePath: string = PAGES_BASE,
): Promise<PageListResponse> => {
  const { data } = await apiClient.get<PageListResponse>(basePath, { params: query });
  return data;
};

export const getPage = async (
  id: string,
  basePath: string = PAGES_BASE,
): Promise<PageDetailWithSections> => {
  const { data } = await apiClient.get<PageDetailWithSections>(`${basePath}/${id}`);
  return data;
};

export const createPage = async (
  payload: CreateWebsitePageRequest,
  basePath: string = PAGES_BASE,
): Promise<WebsitePageDetail> => {
  const { data } = await apiClient.post<WebsitePageDetail>(basePath, payload);
  return data;
};

export const updatePage = async (
  id: string,
  payload: UpdateWebsitePageRequest,
  basePath: string = PAGES_BASE,
): Promise<WebsitePageDetail> => {
  const { data } = await apiClient.patch<WebsitePageDetail>(`${basePath}/${id}`, payload);
  return data;
};

export const publishPage = async (
  id: string,
  basePath: string = PAGES_BASE,
): Promise<WebsitePageDetail> => {
  const { data } = await apiClient.post<WebsitePageDetail>(`${basePath}/${id}/publish`);
  return data;
};

export const unpublishPage = async (
  id: string,
  basePath: string = PAGES_BASE,
): Promise<WebsitePageDetail> => {
  const { data } = await apiClient.post<WebsitePageDetail>(`${basePath}/${id}/unpublish`);
  return data;
};

export const deletePage = async (id: string, basePath: string = PAGES_BASE): Promise<void> => {
  await apiClient.delete(`${basePath}/${id}`);
};
